// Potential Client Questionnaire — public fill page (Cluster B, 2026-08-29;
// Questionnaire v2, 2026-10-21).
//
// Route: /questionnaire/:publicId   (public — no Layout, no auth)
//
// Mirrors EngagementSign.js:
//   • the token lives in the URL FRAGMENT (#t=…).  We read it on mount,
//     persist it to sessionStorage (tab-scoped) so a refresh survives, and
//     history.replaceState-scrub the fragment BEFORE the first API call so
//     it never reaches a Referer header, the address bar, or server logs.
//   • the token is only ever POSTed in a request body (or, for an upload,
//     the X-Qnr-Token header) — never a URL.
//
// Talks to /api/public/questionnaire/* with plain fetch (NOT services/api.js —
// its 401 interceptor redirects to /login, which is wrong for a public page
// where 401 means "your questionnaire session timed out; re-verify").
//
// States: loading / missing_token / unavailable / expired / answers_unavailable /
//         email_code / form / submitting / submitted / already_submitted / error
//
// Everything the server returns is rendered as text (React escapes it).  The
// only HTML rendered raw is the firm's own intro / thank-you copy, which the
// staff API sanitizes on write — never anything a client typed.
//
// Mobile-first: this is filled on a phone.  Single column, big tap targets,
// sticky action bar, explicit "Save progress" with a "Saved HH:MM" stamp, and
// a single inline "this is final" confirmation before submit (never
// window.confirm — Electron throws on it, and it is not the app's pattern).
//
// Questionnaire v2 (docs/specs/questionnaire-v2-civil-rights.md):
//   • typed questions (components/QuestionnaireQuestionList.js) — follow-ups
//     appear only for the triggering answer; hidden answers stay in memory
//     and only VISIBLE answers are sent (the server refuses hidden ones);
//   • the contact block — details on file shown partly hidden with
//     "Correct? Yes / Change"; corrections go to the encrypted answers only;
//   • the upload step (components/QuestionnaireUploadStep.js) — Submit is
//     disabled while files are in flight;
//   • autosave — at most every 2 minutes while there are unsaved changes, and
//     when the phone switches apps (visibilitychange → hidden), on its own
//     endpoint and rate budget.  Drafts are NEVER kept on the device
//     (sessionStorage holds only the link token and the "submitted" flag);
//   • 503 answers_unavailable — the page never shows a blank form it could
//     save over the client's answers: at open it shows a "temporarily
//     unavailable" screen, mid-session it freezes the form read-only;
//   • validation errors come back as {question_id, rule} (never values) and
//     are shown on the question and in a linked summary.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import QuestionnaireQuestionList, { visibleFollowUps } from '../components/QuestionnaireQuestionList'
import QuestionnaireUploadStep from '../components/QuestionnaireUploadStep'
import {
  indexTree, visibleAnswers, contactPayload, missingRequired, ruleMessage, questionTitle, localToday,
} from '../lib/questionnaireTree'

const NARRATIVE_KEYS = ['who', 'what', 'where', 'when', 'why', 'how']
const NARRATIVE_MAX_DEFAULT = 5000
const AUTOSAVE_DEFAULT_SECONDS = 120
// fetch keepalive (so a save survives the tab being frozen) refuses bodies
// over 64 KB; bigger saves go as an ordinary request.
const KEEPALIVE_MAX_BYTES = 60 * 1024

function apiBase () {
  const cfg = process.env.REACT_APP_API_URL || ''
  try { if (cfg && new URL(cfg).origin === window.location.origin) return '' } catch { /* fall through */ }
  return cfg
}

async function call (path, body, { keepalive = false } = {}) {
  const json = body === undefined ? undefined : JSON.stringify(body)
  const res = await fetch(`${apiBase()}/api/public/questionnaire/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    credentials: 'include',            // the HttpOnly session cookie set by /verify
    body: json,
    ...(keepalive && json && json.length < KEEPALIVE_MAX_BYTES ? { keepalive: true } : {}),
  })
  let data = null
  try { data = await res.json() } catch { data = null }
  return { ok: res.ok, status: res.status, data: data || {} }
}

function fmtTime (iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
}
function fmtDate (iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' }) } catch { return '' }
}

// Module-scope shells — NEVER define these inside the component: a new
// component type per render remounts the subtree and every keystroke would
// drop focus in the textareas.
function Shell ({ children, wide = false }) {
  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8 px-3 sm:px-4">
      <div className={`${wide ? 'max-w-2xl' : 'max-w-lg'} mx-auto bg-white rounded-2xl shadow-lg overflow-hidden`}>
        {children}
      </div>
    </div>
  )
}
function Header ({ firmName, title, subtitle }) {
  return (
    <div className="border-b border-gray-200 px-5 py-4 bg-gray-50">
      {firmName && <div className="text-xs uppercase tracking-wide text-gray-500">{firmName}</div>}
      <h1 className="text-lg font-bold break-words">{title || 'Intake Questionnaire'}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
  )
}
function Centered ({ icon, title, children }) {
  return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3" aria-hidden="true">{icon}</div>
        <h1 className="text-xl font-bold mb-2">{title}</h1>
        {children}
      </div>
    </Shell>
  )
}

// The linked error summary (GOV.UK pattern): a link per question, focus moves
// here on a failed submit.  Links scroll + focus the question in-page; they
// never change location.hash — a fragment left in the address bar would be
// read back as the link token on the next reload.
function ErrorSummary ({ items, general, summaryRef }) {
  if (!items.length && !general) return null
  const jump = (e, it) => {
    e.preventDefault()
    const box = document.getElementById(`qnr-q-${it.id}`)
    if (box) {
      if (typeof box.scrollIntoView === 'function') box.scrollIntoView({ behavior: 'smooth', block: 'start' })
      const focusable = box.querySelector('input, textarea, select, button')
      if (focusable && typeof focusable.focus === 'function') focusable.focus({ preventScroll: true })
    }
  }
  return (
    <div ref={summaryRef} role="alert" aria-labelledby="qnr-errsum-h" tabIndex={-1}
      className="border-2 border-red-500 bg-red-50 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-red-400">
      <h2 id="qnr-errsum-h" className="text-base font-bold text-red-800">Please check these answers</h2>
      {general && <p className="text-sm text-red-800 mt-1">{general}</p>}
      {items.length > 0 && (
        <ul className="mt-2 space-y-1.5 list-disc pl-5">
          {items.map(it => (
            <li key={it.id} className="text-sm">
              <a href={`#qnr-q-${it.id}`} onClick={e => jump(e, it)} className="text-red-800 underline break-words">
                {it.title} — {it.message}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// Server errors [{question_id, rule, row?, field?, contact_key?}] → the map the
// renderer reads: { [qid]: { rule, rows: { [i]: { [field]: rule } } } }.
function errorsFromServer (list) {
  const out = {}
  let general = null
  for (const e of list || []) {
    if (!e || e.question_id == null) { if (e && e.rule) general = e.rule; continue }
    const k = String(e.question_id)
    const cur = out[k] || { rule: null, rows: null }
    if (e.row != null && e.field) {
      cur.rows = { ...(cur.rows || {}) }
      cur.rows[e.row] = { ...(cur.rows[e.row] || {}), [e.field]: e.rule }
      if (!cur.rule) cur.rule = null
    } else if (e.row != null) {
      cur.rule = cur.rule || e.rule
    } else cur.rule = e.rule
    out[k] = cur
  }
  return { errors: out, general }
}

export default function QuestionnaireFill () {
  const { publicId } = useParams()
  const tokenKey = `questionnaire_token:${publicId}`
  const doneKey = `questionnaire_done:${publicId}`

  // 1) fragment → sessionStorage → scrub (before any API call)
  const token = useMemo(() => {
    const raw = window.location.hash || ''
    const m = raw.match(/^#(?:t=)?([A-Za-z0-9_\-.=]+)$/)
    if (m) {
      try { sessionStorage.setItem(tokenKey, m[1]) } catch {}
      return m[1]
    }
    try { return sessionStorage.getItem(tokenKey) || '' } catch { return '' }
  }, [tokenKey])
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])

  const [stage, setStage] = useState('loading')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [firmName, setFirmName] = useState('')
  const [form, setForm] = useState(null)          // /verify payload
  const [checked, setChecked] = useState({})      // { [itemId]: true }
  const [narr, setNarr] = useState({})            // { who: '', … }
  const [answers, setAnswers] = useState({})      // v2 typed answers, hidden ones included (memory only)
  const [contact, setContact] = useState({})      // v2 contact answers
  const [fieldErrors, setFieldErrors] = useState({})
  const [generalError, setGeneralError] = useState(null)
  const [summaryOn, setSummaryOn] = useState(false)
  const [summaryNonce, setSummaryNonce] = useState(0)   // bumps when a NEW summary should take focus
  const [announce, setAnnounce] = useState('')
  const [revision, setRevision] = useState(0)
  const [lastSaved, setLastSaved] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [autoState, setAutoState] = useState('')  // '' | 'saving' | 'failed' | 'invalid' | 'stopped'
  const [uploadsBusy, setUploadsBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [missing, setMissing] = useState([])      // required checklist item ids still unchecked (from server or local)
  const [submittedInfo, setSubmittedInfo] = useState(null)
  // Verifier B-4: when the firm voids the link (or it expires) MID-SESSION we
  // must never unmount the form — the typed answers would be unrecoverable.
  // lockedMsg keeps the form mounted read-only so the client can copy them.
  // v2: also set when the server cannot use the answer key (503).
  const [lockedMsg, setLockedMsg] = useState('')
  const [eraseDate, setEraseDate] = useState('')   // expired link: when saved answers are removed (server's display string)
  // email-code state
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [codeBusy, setCodeBusy] = useState(false)
  const [cooldownEnds, setCooldownEnds] = useState(0)
  const [now, setNow] = useState(Date.now())
  const formTopRef = useRef(null)
  const summaryRef = useRef(null)
  // Edits since the last successful save (so a save that raced more typing
  // leaves the page dirty) and when the last save was attempted.
  const editSeqRef = useRef(0)
  const lastSaveAtRef = useRef(Date.now())
  const writingRef = useRef(false)
  const dirtyRef = useRef(false)
  dirtyRef.current = dirty

  useEffect(() => { document.title = firmName ? `${firmName} — Intake Questionnaire` : 'Intake Questionnaire' }, [firmName])

  useEffect(() => {
    if (cooldownEnds <= now) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [cooldownEnds, now])
  const cooldownLeft = Math.max(0, Math.ceil((cooldownEnds - now) / 1000))

  // Warn on tab close with unsaved text (native prompt — not window.confirm).
  useEffect(() => {
    if (!dirty) return
    const h = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [dirty])

  const applyVerified = useCallback((d) => {
    setForm(d)
    setFirmName(d.firm_name || '')
    setRevision(d.revision || 0)
    setLastSaved(d.last_saved_at || null)
    setChecked(d.checklist || {})
    const n = {}
    for (const k of NARRATIVE_KEYS) n[k] = (d.narratives && d.narratives[k]) || ''
    setNarr(n)
    setAnswers(d.answers && typeof d.answers === 'object' ? d.answers : {})
    setContact((d.contact && d.contact.answers && typeof d.contact.answers === 'object') ? d.contact.answers : {})
    setFieldErrors({}); setGeneralError(null); setSummaryOn(false)
    setAutoState('')               // a fresh load (incl. after a stale_revision) resumes autosave
    setDirty(false)
    editSeqRef.current = 0
    lastSaveAtRef.current = Date.now()
    setStage('form')
  }, [])

  // Map a non-2xx verify/save/submit response onto a page state.  Returns
  // true when it consumed the response (state changed).
  const routeFailure = useCallback((r, { fromWrite = false } = {}) => {
    const c = r.data?.code
    const formLoaded = !!form
    if (r.status === 503 && c === 'answers_unavailable') {
      // Fail closed: never a blank form, never a save over answers we could
      // not read.  Mid-session the typed text stays on screen, read-only.
      const msg = r.data?.error || 'Your questionnaire is temporarily unavailable.  Your saved answers are safe — please try again in a little while, or call the office.'
      if (fromWrite && formLoaded) { setLockedMsg(msg); setAutoState('stopped'); return true }
      setError(msg); setStage('answers_unavailable'); return true
    }
    if (r.status === 404) {
      if (fromWrite && formLoaded) {
        // Verifier B-4 — keep the form mounted (read-only) instead of
        // swapping to the unavailable screen and destroying the typed text.
        setLockedMsg('This questionnaire link is no longer available — the firm may have cancelled it or sent you a new link.  Your answers below are preserved on this page only: please copy anything you need, then use the newest email from the firm, or contact the firm for a fresh link.')
        return true
      }
      setStage('unavailable'); return true
    }
    if (r.status === 410 && c === 'expired') {
      // An unreturned link's saved answers are erased at expiry + the
      // retention period: say when (the server's date), never "still there".
      const until = typeof r.data?.erase_date_display === 'string' ? r.data.erase_date_display : ''
      if (fromWrite && formLoaded) {
        setLockedMsg('This link expired before your last change could be saved.  Your answers below are preserved on this page — copy anything you need, then ask the firm to resend the link.' +
          (until ? `  Previously saved progress is kept until ${until}, then removed from our systems.` : ''))
        return true
      }
      setEraseDate(until)
      setStage('expired'); return true
    }
    if ((r.status === 410 || r.status === 409) && c === 'already_submitted') {
      setSubmittedInfo({ submitted_at: r.data.submitted_at })
      try { sessionStorage.removeItem(tokenKey) } catch {}
      setStage('already_submitted'); return true
    }
    return false
  }, [tokenKey, form])

  // /verify — used at boot AND to silently re-mint an expired session.
  const verify = useCallback(async (extra = {}) => {
    const r = await call(`${publicId}/verify`, { token, ...extra })
    if (r.ok) {
      if (r.data.code_required) {
        setFirmName(r.data.firm_name || '')
        setForm({ form_title: r.data.form_title, sent_to_masked: r.data.sent_to_masked, code_active: r.data.code_active })
        setStage('email_code')
        return { ok: true, codeRequired: true }
      }
      applyVerified(r.data)
      return { ok: true }
    }
    if (routeFailure(r)) return { ok: false, routed: true }
    return { ok: false, r }
  }, [publicId, token, applyVerified, routeFailure])

  useEffect(() => {
    let cancelled = false
    async function boot () {
      try {
        const shell = await call(publicId)
        if (cancelled) return
        if (shell.ok) setFirmName(shell.data.firm_name || '')
        try {
          const done = sessionStorage.getItem(doneKey)
          if (done) { setSubmittedInfo({ submitted_at: done }); setStage('submitted'); return }
        } catch {}
        if (!token) { setStage('missing_token'); return }
        const v = await verify()
        if (cancelled) return
        if (!v.ok && !v.routed) {
          setStage('error')
          setError(v.r?.data?.error || 'We could not open this questionnaire.  Please use the exact link from the email.')
        }
      } catch {
        if (cancelled) return
        setStage('error'); setError('We could not reach the firm\'s server.  Please check your connection and try again.')
      }
    }
    boot()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, token])

  // ── email code ──
  const requestCode = async () => {
    setError(''); setCodeBusy(true)
    try {
      const r = await call(`${publicId}/request-code`, { token })
      if (r.ok) { setCodeSent(true); setCode(''); setCooldownEnds(Date.now() + 30_000) }
      else if (!routeFailure(r)) setError(r.data?.error || 'Could not send the code.  Please try again.')
    } finally { setCodeBusy(false) }
  }
  const submitCode = async (e) => {
    e.preventDefault()
    setError(''); setCodeBusy(true)
    try {
      const v = await verify({ code: code.trim() })
      if (!v.ok && !v.routed) setError(v.r?.data?.error || 'That code did not work.')
    } finally { setCodeBusy(false) }
  }

  // ── form edits ──
  const items = form?.items || []
  const prompts = form?.prompts || {}
  const narrativeMax = form?.narrative_max || NARRATIVE_MAX_DEFAULT
  const questions = useMemo(() => (Array.isArray(form?.questions) ? form.questions : []), [form])
  const hasQuestions = questions.length > 0
  const contactFields = useMemo(() => (Array.isArray(form?.contact?.fields) ? form.contact.fields : []), [form])
  const narrativesOn = form ? form.narratives_enabled !== false : true
  const autosaveSeconds = Number(form?.autosave?.interval_seconds) || AUTOSAVE_DEFAULT_SECONDS
  const today = useMemo(() => localToday(), [])

  const markEdited = () => { editSeqRef.current += 1; setDirty(true) }

  // Conditional items: an item with depends_on_item_id is shown only while that
  // parent is ticked.  `== null` catches BOTH null and undefined — undefined is
  // what a questionnaire sent before this feature existed actually carries, and
  // `=== null` here would blank the whole checklist for those clients.
  // One level only (the DB refuses deeper), so a parent is always visible and
  // one pass is enough — no ordering, no recursion.
  const visible = (it) => {
    if (!it || it.depends_on_item_id == null) return true
    return !!checked[String(it.depends_on_item_id)]
  }

  // Unticking a parent must also drop its children from local state, not just
  // hide them.  The payload sends `false` for anything hidden, so if we kept the
  // tick here the screen and the record would disagree — and re-ticking the
  // parent would silently bring back an answer the client had retracted.
  const pruneHidden = (map) => {
    const out = { ...map }
    for (const it of items) {
      const pid = it?.depends_on_item_id
      if (pid != null && !out[String(pid)]) delete out[String(it.id)]
    }
    return out
  }

  const toggle = (id) => {
    setChecked(c => {
      const n = { ...c }
      if (n[id]) delete n[id]; else n[id] = true
      return pruneHidden(n)
    })
    setMissing(m => m.filter(x => String(x) !== String(id)))
    markEdited()
  }
  const setNarrative = (k, v) => { setNarr(n => ({ ...n, [k]: v.slice(0, narrativeMax) })); markEdited() }

  // v2 typed answer.  When a yes/no or choice answer reveals or hides
  // follow-ups, a polite live region says so (focus stays where it is).
  const setAnswer = (node, value) => {
    const k = String(node.id)
    if (node.children && node.children.length && (node.type === 'yes_no' || node.type === 'choice')) {
      const before = visibleFollowUps(questions, node, answers).length
      const after = visibleFollowUps(questions, node, { ...answers, [k]: value }).length
      if (after > before) setAnnounce(`${after - before} follow-up question${after - before === 1 ? '' : 's'} shown below.`)
      else if (after < before) setAnnounce(`${before - after} follow-up question${before - after === 1 ? '' : 's'} hidden.`)
    }
    setAnswers(prev => ({ ...prev, [k]: value }))
    setFieldErrors(e => { if (!e[k]) return e; const n = { ...e }; delete n[k]; return n })
    markEdited()
  }
  const setContactAnswer = (key, value) => {
    setContact(prev => {
      const next = { ...prev }
      if (value == null) delete next[key]; else next[key] = value
      return next
    })
    const f = contactFields.find(x => x.key === key)
    if (f) setFieldErrors(e => { const k = String(f.question_id); if (!e[k]) return e; const n = { ...e }; delete n[k]; return n })
    markEdited()
  }

  // The write body.  v1 keys only for a v1 questionnaire, v2 keys only when
  // the snapshot has typed questions / a contact block; narratives only while
  // the template uses them.
  const payload = () => {
    const body = { token, revision }
    if (items.length) {
      const checklist = {}
      // Hidden children go out as an explicit false.  Sending their stale `true`
      // would store an answer to a question the client was never shown, inside a
      // prospective-client record governed by Cal. Rule 1.18 — and the server
      // refuses it anyway, so the save would fail with nothing on screen to
      // explain why.
      for (const it of items) checklist[String(it.id)] = visible(it) && !!checked[String(it.id)]
      body.checklist = checklist
    }
    if (narrativesOn) {
      const narratives = {}
      for (const k of NARRATIVE_KEYS) narratives[k] = narr[k] || ''
      body.narratives = narratives
    }
    if (hasQuestions) body.answers = visibleAnswers(questions, answers)
    if (contactFields.length) body.contact = contactPayload(contactFields, contact)
    return body
  }
  // The newest payload builder, for the autosave timer and the
  // visibilitychange handler (they must never send a stale closure).
  const payloadRef = useRef(payload)
  payloadRef.current = payload

  // Verifier B-3: the old path called verify() → applyVerified() on a 401,
  // which RESET checked/narr to the last-saved server state before retrying —
  // the screen reverted under the client and a later submit could permanently
  // overwrite their edits with the stale on-screen text.  refreshSession only
  // re-mints the cookie + picks up the server revision; it NEVER touches the
  // on-screen answers.
  const refreshSession = useCallback(async () => {
    const r = await call(`${publicId}/verify`, { token })
    if (r.ok) {
      if (r.data.code_required) { setStage('email_code'); return { ok: false, codeRequired: true } }
      setRevision(r.data.revision || 0)
      setLastSaved(r.data.last_saved_at || null)
      return { ok: true, revision: r.data.revision || 0 }
    }
    if (routeFailure(r, { fromWrite: true })) return { ok: false, routed: true }
    return { ok: false, r }
  }, [publicId, token, routeFailure])

  // One write with a single silent session refresh on 401, and a reload of
  // the latest saved state on 409 stale_revision (another tab saved).
  const write = async (path, opts = {}) => {
    let r = await call(`${publicId}/${path}`, payloadRef.current(), opts)
    if (r.status === 401 && r.data?.code === 'session_expired') {
      const v = await refreshSession()
      if (!v.ok) return r
      // retry with the user's CURRENT on-screen edits, at the fresh revision
      r = await call(`${publicId}/${path}`, { ...payloadRef.current(), revision: v.revision }, opts)
    }
    return r
  }

  // `summary` false for autosave: it marks the questions but never moves the
  // focus away from the box being typed in.
  const raiseSummary = () => { setSummaryOn(true); setSummaryNonce(n => n + 1) }
  const showValidation = (r, { summary = true } = {}) => {
    const { errors, general } = errorsFromServer(r.data?.errors)
    setFieldErrors(errors)
    setGeneralError(general ? ruleMessage(general) : null)
    if (summary) raiseSummary()
  }

  // A successful save of the edits made up to `seq`.
  const saved = (data, seq) => {
    setRevision(data.revision); setLastSaved(data.last_saved_at)
    if (editSeqRef.current === seq) setDirty(false)
  }

  // An explicit Save / Submit waits for an autosave already on the wire:
  // two writes at one revision would make the second a 409 stale_revision,
  // whose recovery reloads the saved state over the screen.
  const waitForWrites = async () => {
    for (let i = 0; i < 100 && writingRef.current; i++) await new Promise(resolve => setTimeout(resolve, 100))
  }

  const saveProgress = async () => {
    setError(''); setNotice(''); setSaving(true)
    await waitForWrites()
    writingRef.current = true
    const seq = editSeqRef.current
    lastSaveAtRef.current = Date.now()
    try {
      const r = await write('save')
      if (r.ok) {
        saved(r.data, seq)
        setAutoState('')
        setFieldErrors({}); setGeneralError(null); setSummaryOn(false)
        setNotice('Progress saved.')
        return true
      }
      if (routeFailure(r, { fromWrite: true })) return false
      if (r.status === 409 && r.data?.code === 'stale_revision') {
        setNotice(r.data.error)
        await verify()          // loads the newer saved state
        return false
      }
      if (r.status === 400 && (r.data?.code === 'invalid_answers' || r.data?.code === 'invalid_contact')) {
        showValidation(r)
        setError(r.data.error || 'Some answers could not be saved.')
        return false
      }
      setError(r.data?.error || r.data?.message || 'Could not save.  Please try again.')
      return false
    } catch {
      setError('Could not reach the server.  Your answers are still on this page — please try again.')
      return false
    } finally { setSaving(false); writingRef.current = false }
  }

  // ── autosave (decision 8) ──
  // Quiet by design: it never reloads the form, never scrolls, and on a
  // conflict it stops and leaves the explicit Save to sort it out.
  const autosave = useCallback(async ({ keepalive = false } = {}) => {
    if (writingRef.current) return
    writingRef.current = true
    const seq = editSeqRef.current
    lastSaveAtRef.current = Date.now()
    setAutoState('saving')
    try {
      const r = await write('autosave', { keepalive })
      if (r.ok) { saved(r.data, seq); setAutoState(''); return }
      if (routeFailure(r, { fromWrite: true })) { setAutoState('stopped'); return }
      if (r.status === 409 && r.data?.code === 'stale_revision') {
        setAutoState('stopped')
        setNotice('This questionnaire was saved from another window, so automatic saving has paused.  Press Save progress to continue.')
        return
      }
      if (r.status === 400 && (r.data?.code === 'invalid_answers' || r.data?.code === 'invalid_contact')) {
        showValidation(r, { summary: false })
        setAutoState('invalid')
        return
      }
      setAutoState('failed')            // 429 / 5xx / offline: the next tick retries
    } catch {
      setAutoState('failed')
    } finally { writingRef.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, token, routeFailure, refreshSession])
  const autosaveRef = useRef(autosave)
  autosaveRef.current = autosave

  const formLive = stage === 'form' && !lockedMsg && autoState !== 'stopped'
  // Every ≤ 2 minutes while there are unsaved changes.  Keyed on `dirty`, so
  // continuous typing does not keep pushing the save back.
  useEffect(() => {
    if (!dirty || !formLive) return undefined
    const wait = Math.max(1000, lastSaveAtRef.current + autosaveSeconds * 1000 - Date.now())
    const t = setTimeout(function tick () { autosaveRef.current() }, wait)
    return () => clearTimeout(t)
  }, [dirty, formLive, autosaveSeconds, autoState, lastSaved])
  // …and when the phone switches apps / the tab is hidden.
  useEffect(() => {
    if (!formLive) return undefined
    const h = () => {
      if (document.visibilityState === 'hidden' && dirtyRef.current) autosaveRef.current({ keepalive: true })
    }
    document.addEventListener('visibilitychange', h)
    return () => document.removeEventListener('visibilitychange', h)
  }, [formLive])

  // Same visibility rule the server applies in missingRequiredItems().  If these
  // two ever disagree, Submit disables over a checkbox that is not on the page
  // and the client has no way to find it.
  const localMissing = items.filter(it => it.is_required && visible(it) && !checked[String(it.id)])
  const index = useMemo(() => indexTree(questions), [questions])
  const summaryItems = useMemo(() => {
    const out = []
    for (const node of index.order) {
      const e = fieldErrors[String(node.id)]
      if (!e) continue
      let rule = e.rule
      if (!rule && e.rows) { const firstRow = Object.values(e.rows)[0] || {}; rule = Object.values(firstRow)[0] }
      out.push({ id: node.id, title: questionTitle(node), message: ruleMessage(rule || 'type', node) })
    }
    return out
  }, [index, fieldErrors])

  // Focus moves to the summary only when one is RAISED (Save / Submit), never
  // when a field error clears as the client types.
  useEffect(() => {
    if (summaryNonce && summaryRef.current && typeof summaryRef.current.focus === 'function') summaryRef.current.focus()
  }, [summaryNonce])

  const beginSubmit = () => {
    setError(''); setNotice('')
    if (localMissing.length) {
      setMissing(localMissing.map(it => it.id))
      setError(`Please complete the ${localMissing.length} required item${localMissing.length === 1 ? '' : 's'} marked below before submitting.`)
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    if (hasQuestions) {
      const miss = missingRequired(questions, { answers, contact })
      if (miss.length) {
        const errs = {}
        for (const n of miss) errs[String(n.id)] = { rule: 'required', rows: null }
        setFieldErrors(errs); setGeneralError(null); raiseSummary()
        return
      }
    }
    setConfirming(true)
  }
  const confirmSubmit = async () => {
    setConfirming(false); setStage('submitting'); setError('')
    await waitForWrites()
    writingRef.current = true
    try {
      const r = await write('submit')
      if (r.ok) {
        try { sessionStorage.removeItem(tokenKey); sessionStorage.setItem(doneKey, r.data.submitted_at || new Date().toISOString()) } catch {}
        setSubmittedInfo({ submitted_at: r.data.submitted_at, thank_you_html: r.data.thank_you_html, firm_name: r.data.firm_name, files_received: r.data.files_received })
        setDirty(false)
        setStage('submitted')
        return
      }
      setStage('form')
      if (routeFailure(r, { fromWrite: true })) return
      if (r.status === 400 && r.data?.code === 'required_items') {
        setMissing((r.data.missing || []).map(m => m.id))
        setError(r.data.error)
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      if (r.status === 400 && r.data?.code === 'required_answers') {
        const errs = {}
        for (const m of r.data.missing || []) errs[String(m.question_id)] = { rule: 'required', rows: null }
        setFieldErrors(errs); setGeneralError(null); raiseSummary()
        return
      }
      if (r.status === 400 && (r.data?.code === 'invalid_answers' || r.data?.code === 'invalid_contact')) {
        showValidation(r)
        return
      }
      if (r.status === 409 && r.data?.code === 'stale_revision') { setNotice(r.data.error); await verify(); return }
      setError(r.data?.error || r.data?.message || 'Could not submit.  Please try again.')
    } catch {
      setStage('form')
      setError('Could not reach the server.  Your answers are still on this page — please try again.')
    } finally { writingRef.current = false }
  }

  const consentUpload = useCallback((initials) => call(`${publicId}/upload-consent`, { token, initials }), [publicId, token])
  const refreshForUpload = useCallback(async () => (await refreshSession()).ok === true, [refreshSession])

  if (stage === 'loading') return <Shell><div className="p-8 text-center text-gray-500">Loading your questionnaire…</div></Shell>

  if (stage === 'missing_token') return (
    <Centered icon="🔗" title="Missing verification">
      <p className="text-gray-600">This link is missing its verification code.  Please open the exact link from the email — the part after <code>#</code> is required.</p>
    </Centered>
  )

  if (stage === 'unavailable') return (
    <Centered icon="📄" title="This questionnaire is no longer available">
      <p className="text-gray-600">The firm may have sent you a newer link — each new email replaces the one before, so please use the most recent one.  The link may also have been cancelled, or it is not the exact link from the email.  If you entered the wrong link several times, wait 15 minutes and try the exact link again.</p>
      <p className="text-gray-500 text-sm mt-4">Please contact the firm if you need a fresh link.</p>
    </Centered>
  )

  if (stage === 'answers_unavailable') return (
    <Centered icon="🛠️" title="Temporarily unavailable">
      <p className="text-gray-600">{error}</p>
      <p className="text-gray-500 text-sm mt-4">Nothing you saved has been lost.  Please use the same link again later.</p>
    </Centered>
  )

  if (stage === 'expired') return (
    <Centered icon="⏳" title="This link has expired">
      <p className="text-gray-600">Questionnaire links work for a limited time.  Please ask the firm to send you a new one{eraseDate ? <> — anything you already saved is kept until {eraseDate}, then removed from our systems.</> : '.'}</p>
    </Centered>
  )

  if (stage === 'error') return (
    <Centered icon="⚠️" title="Link problem">
      <p className="text-gray-600">{error}</p>
      <p className="text-gray-500 text-sm mt-4">If you received this link from the firm, please open the exact URL from the email.  Contact the firm if the problem persists.</p>
    </Centered>
  )

  if (stage === 'already_submitted') return (
    <Centered icon="✅" title="Already submitted">
      <p className="text-gray-600">This questionnaire was submitted{submittedInfo?.submitted_at ? ` on ${fmtDate(submittedInfo.submitted_at)}` : ''} and can no longer be changed.</p>
      <p className="text-gray-500 text-sm mt-4">If you need to add or correct something, please contact the firm and they can reopen it for you.</p>
    </Centered>
  )

  if (stage === 'submitted') return (
    <Shell>
      <div className="p-8 text-center">
        <div className="text-5xl mb-3" aria-hidden="true">✅</div>
        <h1 className="text-xl font-bold mb-2">Thank you</h1>
        {submittedInfo?.thank_you_html
          ? <div className="prose prose-sm max-w-none text-left mt-4" dangerouslySetInnerHTML={{ __html: submittedInfo.thank_you_html }} />
          : <p className="text-gray-600">Your questionnaire has been sent to {submittedInfo?.firm_name || firmName || 'the firm'}.  Someone will be in touch.</p>}
        {Number(submittedInfo?.files_received) > 0 && (
          <p className="text-gray-600 mt-3">We also received {submittedInfo.files_received} {Number(submittedInfo.files_received) === 1 ? 'file' : 'files'} with it.</p>
        )}
        <p className="text-gray-500 text-xs mt-6">Submitted {submittedInfo?.submitted_at ? fmtDate(submittedInfo.submitted_at) : 'just now'}.  This questionnaire is now final; contact the firm if anything needs to change.</p>
      </div>
    </Shell>
  )

  if (stage === 'email_code') return (
    <Shell>
      <Header firmName={firmName} title={form?.form_title} subtitle="For your privacy, please confirm it's you before we open the questionnaire." />
      <form onSubmit={submitCode} className="p-5 space-y-4">
        {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
          <div className="text-sm">
            <div className="font-medium">Step 1 — Get a verification code</div>
            <div className="text-xs text-gray-500">We'll email a 6-digit code to <strong>{form?.sent_to_masked}</strong>.</div>
          </div>
          <button type="button" onClick={requestCode} disabled={cooldownLeft > 0 || codeBusy}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-3 hover:bg-gray-50 disabled:opacity-50">
            {cooldownLeft > 0 ? `📧 Resend in ${cooldownLeft}s` : codeSent ? '📧 Resend code' : '📧 Email me a code'}
          </button>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
          <div className="text-sm font-medium">Step 2 — Enter the code</div>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required maxLength={6} pattern="^\d{6}$" inputMode="numeric" autoComplete="one-time-code"
            placeholder="6-digit code" aria-label="6-digit verification code"
            className="w-full border border-gray-300 rounded-lg px-3 py-3 text-lg font-mono tracking-widest text-center" />
          <button type="submit" disabled={code.length !== 6 || codeBusy}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 rounded-lg disabled:opacity-50">
            {codeBusy ? 'Checking…' : 'Open my questionnaire'}
          </button>
        </div>
      </form>
    </Shell>
  )

  // ── form / submitting ──
  const busy = stage === 'submitting' || !!lockedMsg
  const missingSet = new Set(missing.map(String))
  const uploadInfo = form?.uploads || null
  const statusText = stage === 'submitting' ? 'Submitting…'
    : saving || autoState === 'saving' ? 'Saving…'
      : autoState === 'invalid' && dirty ? <span className="text-red-700">Not saved — please check the highlighted answers</span>
        : autoState === 'failed' && dirty ? <span className="text-red-700">Not saved — retrying</span>
        : dirty ? <span className="text-amber-700">Unsaved changes</span>
          : lastSaved ? `Saved ${fmtTime(lastSaved)}` : 'Not saved yet'
  return (
    <Shell wide>
      <Header firmName={firmName} title={form?.form_title} subtitle="Your answers are private to the firm.  Your progress saves as you go — you can come back later with the same link." />
      <div ref={formTopRef} />
      {form?.intro_html && <div className="px-5 pt-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: form.intro_html }} />}
      <div className="sr-only" aria-live="polite">{announce}</div>

      <div className="px-4 sm:px-5 py-4 space-y-6 pb-32">
        {lockedMsg && <div role="alert" className="bg-amber-50 border border-amber-300 text-amber-900 rounded-lg p-3 text-sm">{lockedMsg}</div>}
        {summaryOn && <ErrorSummary items={summaryItems} general={generalError} summaryRef={summaryRef} />}
        {error && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        {notice && !error && <div role="status" className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm">{notice}</div>}

        {items.length > 0 && (
          <section aria-labelledby="qnr-checklist">
            <h2 id="qnr-checklist" className="text-base font-bold mb-1">Checklist</h2>
            <p className="text-xs text-gray-500 mb-3">Tick everything that applies.  Items marked <span className="text-red-600 font-semibold">*</span> are required.  Some answers reveal a follow-up question.</p>
            <ul className="space-y-2">
              {items.filter(visible).map(it => {
                const id = String(it.id)
                const isMissing = missingSet.has(id)
                // A revealed child is indented and rule-marked so it reads as a
                // follow-up to the item above it, not a new top-level question.
                const isChild = it.depends_on_item_id != null
                return (
                  <li key={id} className={isChild ? 'ml-6 border-l-2 border-gray-200 pl-3' : undefined}>
                    <label className={`flex items-start gap-3 border rounded-lg px-3 py-3 cursor-pointer ${isMissing ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={!!checked[id]} onChange={() => toggle(id)} disabled={busy}
                        className="mt-1 h-5 w-5 flex-shrink-0" aria-required={!!it.is_required} />
                      <span className="text-sm">
                        <span className="font-medium">{it.label}</span>
                        {it.is_required && <span className="text-red-600 font-semibold ml-1" aria-label="required">*</span>}
                        {it.help_text && <span className="block text-xs text-gray-500 mt-0.5">{it.help_text}</span>}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        )}

        {hasQuestions && (
          <QuestionnaireQuestionList
            questions={questions} answers={answers} onAnswer={setAnswer}
            contactFields={contactFields} contact={contact} onContact={setContactAnswer}
            errors={fieldErrors} disabled={busy} today={today}
            renderUpload={(node) => (uploadInfo && String(uploadInfo.question_id) === String(node.id)
              ? <QuestionnaireUploadStep node={node} info={uploadInfo} token={token}
                  uploadUrl={`${apiBase()}/api/public/questionnaire/${publicId}/upload`}
                  onConsent={consentUpload} onRefreshSession={refreshForUpload}
                  onBusyChange={setUploadsBusy} disabled={busy} />
              : (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 text-sm text-gray-600">
                  <div className="font-medium text-gray-900 mb-1">{questionTitle(node)}</div>
                  The upload step is not available on this link.  Please call the office to send documents.
                </div>
              ))} />
        )}

        {narrativesOn && (
          <section aria-labelledby="qnr-story">
            <h2 id="qnr-story" className="text-base font-bold mb-1">In your own words</h2>
            <p className="text-xs text-gray-500 mb-3">Plain language is perfect.  Each box can hold up to {narrativeMax.toLocaleString()} characters.</p>
            <div className="space-y-5">
              {NARRATIVE_KEYS.map(k => {
                const v = narr[k] || ''
                const near = v.length >= narrativeMax * 0.9
                return (
                  <div key={k}>
                    <label htmlFor={`qnr-narr-${k}`} className="block text-sm font-medium mb-1">{prompts[k] || k}</label>
                    <textarea id={`qnr-narr-${k}`} value={v} onChange={e => setNarrative(k, e.target.value)}
                      maxLength={narrativeMax} rows={5} disabled={busy}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-primary-500" />
                    <div className={`text-right text-xs mt-1 ${v.length >= narrativeMax ? 'text-red-600' : near ? 'text-amber-600' : 'text-gray-400'}`} aria-live="polite">
                      {v.length.toLocaleString()} / {narrativeMax.toLocaleString()}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        <section className="border border-amber-200 bg-amber-50 rounded-lg p-4 text-xs text-amber-900 space-y-1">
          <div className="font-semibold">Before you submit</div>
          <p>Completing this questionnaire does not make you a client of the firm and does not create an attorney-client relationship.  It is not a guarantee that the firm can take your matter.</p>
          <p>Legal deadlines may be running now — some are very short.  Do not wait for a reply before protecting your rights.</p>
          <p>Please do not include anything you would not want the firm to keep on file, and do not share another person's private or privileged information.</p>
          {form?.retention?.notice && <p>{form.retention.notice}</p>}
        </section>

        {confirming && (
          <div role="dialog" aria-modal="true" aria-labelledby="qnr-confirm" className="border-2 border-primary-300 bg-primary-50 rounded-xl p-4 space-y-3">
            <h3 id="qnr-confirm" className="font-bold">Submit your questionnaire?</h3>
            <p className="text-sm text-gray-700">This is final.  Once submitted you will not be able to change your answers or send more files here — the firm can reopen it for you if something needs correcting.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={confirmSubmit}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 rounded-lg">
                Yes, submit — this is final
              </button>
              <button type="button" onClick={() => setConfirming(false)}
                className="flex-1 border border-gray-300 bg-white font-medium py-3 rounded-lg hover:bg-gray-50">
                Go back
              </button>
            </div>
          </div>
        )}
      </div>

      {/* sticky action bar — thumb reach on a phone */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        <div className="max-w-2xl mx-auto px-3 py-3 flex items-center gap-2">
          <div className="flex-1 text-xs text-gray-500 min-w-0" aria-live="polite">
            {statusText}
          </div>
          <button type="button" onClick={saveProgress} disabled={saving || busy}
            className="border border-gray-300 bg-white font-medium px-3 sm:px-4 py-3 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm whitespace-nowrap">
            💾 Save progress
          </button>
          <button type="button" onClick={beginSubmit} disabled={saving || busy || confirming || uploadsBusy}
            title={uploadsBusy ? 'Wait for your files to finish sending' : undefined}
            className="bg-primary-600 hover:bg-primary-700 text-white font-medium px-3 sm:px-4 py-3 rounded-lg disabled:opacity-50 text-sm whitespace-nowrap">
            {stage === 'submitting' ? 'Submitting…' : 'Review & submit'}
          </button>
        </div>
      </div>
    </Shell>
  )
}
